import React, { useEffect, useState } from "react";

import { formatNumber, formatNumericCurrency, useTranslation } from "utils";

import { CurrencyCode } from "consts";

import { Flex, Icon, Slider, Typography } from "components";

type PayoutContextCalculationCardProps = {
  currency?: CurrencyCode;
  co2Baseline: number;
  hasAllotment: boolean;
  pricePerT: number;
  actualPayout: number;
};

const formatTonnes = (value: number) => `${formatNumber({ number: value })}t`;

export const PayoutContextCalculationCard = ({ currency, co2Baseline, hasAllotment, pricePerT, actualPayout }: PayoutContextCalculationCardProps) => {
  const { t } = useTranslation("fieldTimeline");

  const baselineForCalculation = hasAllotment ? co2Baseline : 0;
  const impactAtActualPayout = pricePerT > 0 ? actualPayout / pricePerT + baselineForCalculation : baselineForCalculation;
  const sliderMinImpact = 0;
  const sliderMaxImpact = impactAtActualPayout > 0 ? impactAtActualPayout * 2 : 1;
  const [sliderImpact, setSliderImpact] = useState(impactAtActualPayout);
  const step = 0.01;

  const sliderImpactValue = Math.min(Math.max(sliderImpact, sliderMinImpact), sliderMaxImpact);
  const sliderHandlePct = sliderMaxImpact > sliderMinImpact ? ((sliderImpactValue - sliderMinImpact) / (sliderMaxImpact - sliderMinImpact)) * 100 : 50;
  const impactBeyondBaseline = Math.max(sliderImpactValue - baselineForCalculation, 0);
  const expectedPayout = impactBeyondBaseline * pricePerT;

  useEffect(() => {
    setSliderImpact(impactAtActualPayout);
  }, [impactAtActualPayout]);

  return (
    <Flex vertical className="pcd-card" gap="2rem">
      <Flex vertical className="pcd-calc-header" gap=".5rem">
        <Typography variant="h4">{t("impactPanel.payoutImpact.contextDrawer.howCalculate.title")}</Typography>
        <Typography variant="paragraph-m">{t("impactPanel.payoutImpact.contextDrawer.howCalculate.description")}</Typography>
      </Flex>
      <Flex vertical className="pcd-calc-slider">
        <Flex vertical align="center" className="pcd-impact-values margin-bottom-50">
          <Typography variant="h1" className="impact">{`+${formatTonnes(sliderImpactValue)}`}</Typography>
          <Typography variant="paragraph-s" className="label" color="grey-600">
            {t("impactPanel.payoutImpact.contextDrawer.howCalculate.currentImpact")}
          </Typography>
        </Flex>
        <div className="pcd-impact-slider-wrap" onDoubleClick={() => setSliderImpact(impactAtActualPayout)}>
          <Slider
            className="pcd-impact-slider"
            min={sliderMinImpact}
            max={sliderMaxImpact}
            step={step}
            value={sliderImpactValue}
            onChange={(value) => setSliderImpact(Array.isArray(value) ? value[0] : value)}
            tooltip={{ open: false }}
          />
          <span className="pcd-slider-handle-icons" style={{ left: `${sliderHandlePct}%`, gap: "0" }}>
            <Icon iconName="tabler/chevron-left" size="s" />
            <Icon iconName="tabler/chevron-right" size="s" />
          </span>
        </div>
        <Flex vertical align="center" className="pcd-impact-values margin-top-100">
          <Typography variant="h1" className="impact">
            {formatNumericCurrency(expectedPayout, { decimals: 0, currency })}
          </Typography>
          <Typography variant="paragraph-s" className="label" color="grey-600">
            {t("impactPanel.payoutImpact.contextDrawer.howCalculate.currentPayout")}
          </Typography>
        </Flex>
      </Flex>
      <Flex vertical className="pcd-breakdown pcd-breakdown-edge pcd-breakdown-calc">
        <Flex align="center" className="pcd-breakdown-header" gap="0.25rem">
          <Icon iconName="tabler/chevron-down" size="m-light" />
          <Typography variant="paragraph-m" bold>
            {t("impactPanel.payoutImpact.contextDrawer.howCalculate.expectedBreakdown")}
          </Typography>
        </Flex>
        <Flex vertical className="pcd-breakdown-body pcd-breakdown-body-calc" gap="0.5rem">
          <Flex justify="space-between" className="row">
            <Typography variant="paragraph-m">{t("impactPanel.payoutImpact.contextDrawer.howCalculate.currentImpact")}</Typography>
            <Typography variant="paragraph-m">{formatTonnes(sliderImpactValue)}</Typography>
          </Flex>
          <Flex justify="space-between" className="row">
            <Typography variant="paragraph-m">{t("impactPanel.payoutImpact.contextDrawer.howCalculate.baseline")}</Typography>
            <Typography variant="paragraph-m">{formatTonnes(co2Baseline)}</Typography>
          </Flex>
          <Flex justify="space-between" className="row">
            <Typography variant="paragraph-m">{t("impactPanel.payoutImpact.contextDrawer.howCalculate.impactBeyondBaseline")}</Typography>
            <Typography variant="paragraph-m">{`${formatTonnes(sliderImpactValue)} - ${formatTonnes(co2Baseline)} = ${formatTonnes(
              impactBeyondBaseline
            )}`}</Typography>
          </Flex>
          <Flex justify="space-between" className="row">
            <Typography variant="paragraph-m">{t("impactPanel.payoutImpact.contextDrawer.howCalculate.pricePerT")}</Typography>
            <Typography variant="paragraph-m">{`${formatNumericCurrency(pricePerT, { decimals: 0, currency })} / t`}</Typography>
          </Flex>
          <Flex justify="space-between" className="row">
            <Typography variant="paragraph-m">{t("impactPanel.payoutImpact.contextDrawer.howCalculate.calculation")}</Typography>
            <Typography variant="paragraph-m">{`${formatTonnes(impactBeyondBaseline)} x ${formatNumericCurrency(pricePerT, {
              decimals: 0,
              currency,
            })} / t = ${formatNumericCurrency(expectedPayout, { decimals: 0, currency })}`}</Typography>
          </Flex>
        </Flex>
        <Flex justify="flex-end" className="pcd-breakdown-total margin-top-100">
          <Typography variant="h1">{formatNumericCurrency(expectedPayout, { decimals: 0, currency })}</Typography>
        </Flex>
      </Flex>
    </Flex>
  );
};
